import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type B2cActionStatut = 'planifiee' | 'realisee' | 'annulee' | 'reportee';
export type B2cFormateurPaiementType = 'forfait_fixe' | 'pourcentage_ca';
export type B2cParticipationStatut = 'paye' | 'non_paye' | 'partiel';
export type B2cPaiementMode = 'virement' | 'cheque' | 'especes' | 'ccp' | 'carte';

export interface B2cActionRow {
  id: string;
  reference: string;
  formation_id: string | null;
  formateur_id: string | null;
  date_action: string;
  horaire: string | null;
  lieu: string | null;
  capacite_max: number;
  prix_unitaire_b2c: number;
  formateur_paiement_type: B2cFormateurPaiementType;
  formateur_montant_forfait: number | null;
  formateur_pct_ca: number | null;
  statut: B2cActionStatut;
  observation: string | null;
  created_at: string;
}

export interface B2cParticipationRow {
  id: string;
  action_id: string;
  participant_nom: string;
  participant_telephone: string | null;
  participant_email: string | null;
  prix_paye: number;
  mode_paiement: B2cPaiementMode;
  statut_paiement: B2cParticipationStatut;
  present: boolean;
  created_at: string;
}

export function useB2cActions(filter: { statut?: B2cActionStatut | null }) {
  return useQuery({
    queryKey: ['b2c_actions', filter],
    queryFn: async (): Promise<B2cActionRow[]> => {
      let q = supabase
        .from('b2c_actions_formation')
        .select('*')
        .order('date_action', { ascending: false });
      if (filter.statut) q = q.eq('statut', filter.statut);
      const { data, error } = await q.limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as B2cActionRow[];
    },
  });
}

export function useB2cAction(id: string | null) {
  return useQuery({
    queryKey: ['b2c_action', id],
    enabled: !!id,
    queryFn: async (): Promise<B2cActionRow | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('b2c_actions_formation')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as B2cActionRow) ?? null;
    },
  });
}

export function useB2cParticipations(actionId: string | null) {
  return useQuery({
    queryKey: ['b2c_participations', actionId],
    enabled: !!actionId,
    queryFn: async (): Promise<B2cParticipationRow[]> => {
      if (!actionId) return [];
      const { data, error } = await supabase
        .from('b2c_participations')
        .select('*')
        .eq('action_id', actionId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as B2cParticipationRow[];
    },
  });
}

export function useUpsertB2cAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<B2cActionRow> & { date_action: string; prix_unitaire_b2c: number },
    ): Promise<B2cActionRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('b2c_actions_formation')
          .update(rest as never)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as B2cActionRow;
      }
      const { data, error } = await supabase
        .from('b2c_actions_formation')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as B2cActionRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['b2c_actions'] });
    },
  });
}

export function useAddB2cParticipation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<B2cParticipationRow> & {
        action_id: string;
        participant_nom: string;
        prix_paye: number;
      },
    ): Promise<B2cParticipationRow> => {
      const { data, error } = await supabase
        .from('b2c_participations')
        .insert(input as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as B2cParticipationRow;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['b2c_participations', vars.action_id] });
    },
  });
}
